import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, CheckCircle2, User } from 'lucide-react';
import { useInView } from '../components/UI';

const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const heroMeta = [
  { label: 'Founded', value: '2026, Ibadan, Oyo State, Nigeria' },
  { label: 'Focus', value: 'Electric fleet, BaaS, ride-hailing, and EV manufacturing' },
  { label: 'Launch', value: "Ibadan, Nigeria's third-largest city" },
  { label: 'Vision', value: "Africa's first local EV manufacturer by 2042" },
  { label: 'Model', value: 'Self-funding, each phase financed by the previous' },
  { label: 'Document', value: 'Whitepaper v2.0, available for download' },
];

const originStats = [
  { v: '2026', l: 'Year founded and launched in Ibadan' },
  { v: '500', l: 'Electric motorcycles in Phase 1 rollout' },
  { v: '$6', l: 'Daily payment, proven in the field in Ibadan' },
];

const coreTeam = [
  { role: 'Chief Executive Officer', tag: 'CEO',name:'Odimayo Benjamin' },
  { role: 'Head of Technology & Innovation', tag: 'Tech & Innovation',name:'Ediale Precious' },
  { role: 'Marketing Lead', tag: 'Marketing',name:'Ali Nuhu'},
  { role: 'Social Media Manager', tag: 'Social Expert',name:'Deborah' },
];

const beliefs = [
  {
    num: '01',
    title: 'Africa does not need to repeat the past to grow.',
    desc: 'Every continent that industrialised before Africa did so by burning fossil fuels and exporting the environmental cost. Africa has the opportunity and now the technology and capital to build differently. Azura is designed to demonstrate this is not a trade-off but a long-term advantage.',
  },
  {
    num: '02',
    title: 'Impact and profitability are the same thing here.',
    desc: 'Reducing rider fuel costs increases rider income. Increasing rider income grows the economy. Growing the economy expands the market for Azura. The commercial logic and the social logic point in exactly the same direction. This is not coincidence. It is the business model.',
  },
  {
    num: '03',
    title: 'Systems must be owned locally to serve local people.',
    desc: 'A BaaS network owned by a foreign company takes value away from Nigerian riders and sends it elsewhere. A local company keeps that value in the network: in wages, in investment, in tax, in community. Azura is that company.',
  },
  {
    num: '04',
    title: 'The solution must be designed for African conditions.',
    desc: 'Sub-2-minute battery swaps exist because grid-dependent charging does not work here. The $6/day model exists because that is what field research in Ibadan revealed, not what a spreadsheet in a foreign city assumed. Design for the context, not for the assumption.',
  },
];

const timeline = [
  { year: '2023', title: 'The Inflection Point', desc: "Nigeria removes petrol subsidy. Fuel prices surge 4–6×. The conditions that necessitate Azura become acute.", tag: null },
  { year: '2024', title: 'Field Testing', desc: 'Field research begins in Ibadan. $6/day payment model tested against real rider income and operating costs.', tag: 'Foundational testing' },
  { year: '2025', title: 'Whitepaper v1.0', desc: 'Whitepaper v1.0 published. Initial financial model, BaaS framework, and AZT token structure established.', tag: null },
  { year: 'Dec 2025', title: 'Sankofa Foundry Roadmap', desc: 'Four-stage manufacturing plan from assembling imported kits to full local manufacturing by 2042 published.', tag: 'Manufacturing vision' },
  { year: 'Feb 2026', title: 'Whitepaper v2.0', desc: 'Updated financial model, corrected AZT token structure, expanded Sankofa roadmap published.', tag: null },
  { year: 'Q3 2026', title: '3rd Quarter 2026 Strategic Round', desc: '$500K raised from founding investors. 100M AZT at 4,000 AZT per unit.', tag: 'Upcoming' },
  { year: 'Q4 2026', title: 'Phase 1 Launches', desc: '500 electric motorcycles rolled out. 17 BaaS swap stations operational. Nigeria\'s first integrated EV payment programme.', tag: 'Upcoming' },
  { year: 'Q4 2026', title: 'AZT Presale Opens', desc: '100M tokens at $0.010. Target raise: $1.0M for fleet expansion to Oyo State.', tag: 'Upcoming' },
];

const numbers = [
  { v: '500', l: 'Electric motorcycles rolled out, Ibadan, Phase 1, 2026' },
  { v: '17', l: 'BaaS battery swap stations operational at launch' },
  { v: '$6', l: 'Daily payment, proven in the field in Ibadan' },
  { v: '$1.53M', l: 'Projected Year 1 revenue, base case' },
  { v: '100K', l: 'EVs target by 2036, across 5 African markets' },
  { v: '$264.8M', l: 'Projected revenue by 2036: fleet, BaaS, and EV sales' },
  { v: '2042', l: "Full local manufacturing: Africa's first local EV manufacturer" },
];

const Img: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => (
  <img src={src} alt={alt} className={className}/>
);

/* ══ ABOUT PAGE ══ */
const About: React.FC = () => {
  const { ref: originRef, inView: originInView } = useInView();
  const { ref: beliefRef, inView: beliefInView } = useInView();
  const { ref: timeRef, inView: timeInView } = useInView();
  const { ref: numRef, inView: numInView } = useInView();
  const { ref: teamRef, inView: teamInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/70">/</span>
            <span className="text-accent-amber text-xs font-semibold">About</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
            Built in Africa. For Africa.<br />
            <span className="text-accent-amber">By Africans.</span>
          </h1>

          <p className="max-w-2xl text-white/70 text-base leading-8">
            Azura Mobility is a Nigerian-founded, climate-impact electric mobility company building a fully integrated network of electric vehicles, Battery-as-a-Service infrastructure, ride-hailing services, and a long-term local manufacturing capability across Africa.
          </p>
        </div>
      </section>

      {/* ══ COMPANY META ══ */}
{/*       <section className="py-14 px-6 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
            {heroMeta.map((m, i) => (
              <div key={m.label}
                className="px-6 py-5 border-b border-r border-border last:border-r-0 even:lg:border-r-0 hover:bg-neutral-50 transition-colors">
                <p className="text-xs text-neutral-400 uppercase tracking-widest mb-1.5">{m.label}</p>
                <p className="font-figtree font-bold text-sm text-bg-dark">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ══ THE ORIGIN ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={originRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* image */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
               <Img src="/images/bike4.png" alt="The Origin" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            
              <div className="grid grid-cols-3 gap-3 mt-3">
                {originStats.map(s => (
                  <div key={s.l} className="bg-white border border-border rounded-2xl p-4 shadow-card text-center">
                    <p className="font-figtree font-black text-2xl text-accent-amber mb-0.5">{s.v}</p>
                    <p className="text-xs text-neutral-500 ">{s.l}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <div className={`lg:col-span-7 transition-all duration-700 ${originInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Origin</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-6">
                Why Azura <span className="text-accent-amber italic">was built.</span>
              </h2>

              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-4">
                In 2023, Nigeria removed its petrol subsidy. Fuel prices surged by 400–600% overnight. For the eight million commercial motorcycle operators who depend on those vehicles for their livelihoods, it was not a policy headline. It was a crisis.
              </p>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-6">
                The existing electric vehicle solutions were not designed for this context. Conventional charging required four to eight hours of downtime. Grid reliability in Nigeria makes any charging-dependent model fundamentally fragile. And every motorcycle sold in Africa was manufactured somewhere else.
              </p>

              {/* pull quote */}
              <div className="flex gap-4 p-6 bg-white rounded-2xl border border-border shadow-card mb-6">
                <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
                <div>
                  <span className="font-serif text-3xl text-accent-amber/20 leading-none select-none">"</span>
                  <p className="font-exo text-sm text-bg-dark leading-relaxed italic">The problem was not that Africa lacked electric vehicles. The problem was that no one had built an electric mobility network designed specifically for how Africa actually works.</p>
                </div>
              </div>

              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-4">
                Azura was founded to address that gap — not as a charity, not as a pilot programme, but as a serious commercial operations company. One that proves its numbers in the field before projecting them at scale.
              </p>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed">
                Azura is not a technology company with an Africa strategy. It is an African company, built here, for here, by people who understand this market from the inside. That distinction matters for government partnerships, community trust, eligibility for development-finance funding, and the kind of long-term commitment that infrastructure demands.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHAT WE BELIEVE ══ */}
      <section className="py-20 px-6 bg-white border-t border-border">
        <div ref={beliefRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${beliefInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">What We Believe</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              The convictions that shape<br />
              <span className="text-accent-amber italic">every decision we make.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-5">
              {beliefs.map((b, i) => (
                <div key={b.num}
                  className={`bg-white border border-border rounded-2xl p-7 shadow-card relative overflow-hidden transition-all duration-700 ${beliefInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}>
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                  <span className="absolute bottom-4 right-5 font-figtree font-black text-[4rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">{b.num}</span>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-7 h-7 rounded-full bg-accent-amber-light border border-accent-amber/30 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 size={13} className="text-bg-dark" />
                    </div>
                    <h3 className="font-figtree font-black text-base text-bg-dark leading-snug">{b.title}</h3>
                  </div>
                  <p className="font-exo text-sm text-neutral-500 leading-relaxed pl-10">{b.desc}</p>
                </div>
              ))}
            </div>

            {/* image beside beliefs */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="overflow-hidden aspect-[3/4]">
                <Img src="/images/bike2.png" alt="What we believe" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-figtree font-black text-2xl text-white mb-1">Africa first.</p>
                  <p className="font-exo text-sm text-white/55 leading-relaxed">Not adapted for Africa. Designed for Africa from the first principle.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ COMPANY TIMELINE ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white relative overflow-hidden border-t-4 border-accent-amber">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div ref={timeRef} className="max-w-7xl mx-auto relative z-10">
          <div className={`mb-14 transition-all duration-700 ${timeInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Company Timeline</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight">
              Every step, <span className="text-accent-amber italic">on record.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* timeline */}
            <div className="lg:col-span-7">
              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-gradient-to-b from-accent-amber/60 via-accent-amber/20 to-transparent" />
                <div className="space-y-8">
                  {timeline.map((t, i) => (
                    <div key={i}
                      className={`flex gap-5 items-start pl-12 relative transition-all duration-700 ${timeInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                      style={{ transitionDelay: `${i * 80}ms` }}>
                      <div className="absolute left-0 w-10 h-10 rounded-full bg-bg-dark border border-accent-amber/30 flex items-center justify-center font-bold text-accent-amber text-xs shrink-0 z-10">
                        {t.year.length <= 4 ? t.year : t.year.slice(0, 3)}
                      </div>
                      <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 flex-1">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <p className="text-xs text-accent-amber/60 tracking-widest uppercase mb-1">{t.year}</p>
                            <h4 className="font-figtree font-bold text-sm text-white">{t.title}</h4>
                          </div>
                          {t.tag && (
                            <span className={`shrink-0 text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                              t.tag === 'Live' ? 'bg-green-500/10 border-green-500/30 text-green-400' :
                              t.tag === 'Upcoming' ? 'bg-accent-amber/10 border-accent-amber/30 text-accent-amber' :
                              'bg-white/5 border-white/10 text-white/70'
                            }`}>{t.tag}</span>
                          )}
                        </div>
                        <p className="font-exo text-xs text-white/70 leading-relaxed">{t.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* image beside timeline */}
            <motion.div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="aspect-[3/4] bg-white">
                <Img src="/images/bike3.png" alt="Azura timeline" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ THE NUMBERS ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={numRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${numInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Numbers</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              Where we are.<br />
              <span className="text-accent-amber italic">Where we are going.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {numbers.map((n, i) => (
                <div key={n.l}
                  className={`bg-white border border-border rounded-2xl p-6 shadow-card relative overflow-hidden hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 ${numInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                  <p className="font-figtree font-black text-3xl text-accent-amber mb-1.5 tracking-tight">{n.v}</p>
                  <p className="text-xs text-neutral-500 uppercase leading-snug">{n.l}</p>
                </div>
              ))}
            </div>

            {/* image + cta */}
            <div className="lg:col-span-4 flex flex-col gap-4">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft}>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-dark border border-border group">
                  <Img src="/images/bike5.png" alt="Azura numbers" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  <div className="absolute inset-0" />
                </div>
              </motion.div>

              <div className="bg-bg-dark rounded-2xl p-6 text-center">
                <p className="font-exo text-white/70 text-sm leading-relaxed mb-5">
                  Full revenue projections, funding needed, and rollout plan in the Opportunity page.
                </p>
                <div className="flex flex-col gap-2">
                  <Link to="/opportunity"
                    className="inline-flex items-center justify-center gap-2 bg-accent-amber text-bg-dark px-5 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all">
                    The Opportunity <ArrowRight size={13} />
                  </Link>
                  <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-white/15 text-white px-5 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
                    Download Whitepaper ↗
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CORE TEAM ══ */}
      <section className="py-20 px-6 bg-white border-t border-border">
        <div ref={teamRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${teamInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Meet The Team</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
                Meet the <span className="text-accent-amber italic">Core Team.</span>
              </h2>
              <Link to="/team" className="inline-flex items-center gap-2 text-xs font-figtree font-bold uppercase tracking-wider text-bg-dark hover:text-accent-amber transition-colors shrink-0">
                Full Team Page <ArrowRight size={13} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {coreTeam.map((member, i) => (
              <div key={member.role}
                className={`rounded-2xl border border-dashed border-border bg-neutral-50 p-6 text-center transition-all duration-700 ${teamInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-16 h-16 rounded-full bg-white border border-border flex items-center justify-center mx-auto mb-4 text-neutral-300">
                  <User size={26} />
                </div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-accent-amber bg-accent-amber/10 border border-accent-amber/20 px-2.5 py-1 rounded-full mb-3">{member.tag}</span>
                <p className="font-figtree font-black text-sm text-bg-dark mb-1">{member.name}</p>
                <p className="text-xs text-neutral-400 uppercase tracking-widest">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
            Believe in the mission? <span className="text-accent-amber italic">Buy Azura Token.</span>
          </h2>
          <p className="font-exo text-white/70 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            AZT is how you back this company at the founding stage, not after the story is already written.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Buy Azura Token <ArrowRight size={13} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default About;